import React, { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Users, ChevronLeft, ChevronRight, ChevronDown, Info, Building2, ClipboardList, Camera, Trash2, Activity, Star, Clock } from 'lucide-react';
import { useFilterParams } from "@/store/filtersStore";
import { useUserStore } from '@/store/userStore';
import {
  fetchLiderazgo, fetchLiderazgoResumen, fetchLiderazgoTopPersonas, fetchLiderazgoPorActividad, fetchLiderazgoPorSede,
  fetchFilterSedes, fetchFilterNegocios, fetchFilterActividades,
} from '@/services/api';
import { KpiGlowCard } from '@/components/dashboard/KpiGlowCard';
import { ChartCard } from '@/components/dashboard/ChartCard';
import { HorizontalBarChart } from '@/components/dashboard/Charts';
import { GlobalFilterBar } from '@/components/dashboard/GlobalFilterBar';
import { ModuleFilterBar } from '@/components/common/ModuleFilterBar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import ExportButtons from '@/components/common/ExportButtons';
import { filterSinSede, sinSedeDisclaimer } from '@/utils/sedeUtils';
import { PhotoViewerButton } from '@/components/common/PhotoViewer';
import { TrazabilidadFooter } from '@/components/common/TrazabilidadFooter';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const _DIAS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const getDia = (fecha) => { try { return _DIAS[new Date(fecha + 'T12:00:00').getDay()]; } catch { return '-'; } };

const LID_FILTER_DEFS = [
  { key: 'desde',       label: 'Desde',         type: 'date' },
  { key: 'hasta',       label: 'Hasta',          type: 'date' },
  { key: 'persona',     label: 'Persona',        type: 'text' },
  { key: 'sede',        label: 'Sede',           type: 'select', fetchFn: fetchFilterSedes },
  { key: 'negocio',     label: 'Negocio',        type: 'select', fetchFn: fetchFilterNegocios },
  { key: 'actividad',   label: 'Actividad',      type: 'select', fetchFn: fetchFilterActividades },
  { key: 'incumplimiento', label: 'Incumplimiento', type: 'select', options: ['SI', 'NO'] },
  { key: 'foto',        label: 'Foto',           type: 'select', options: ['Con foto', 'Sin foto'] },
  { key: 'observaciones', label: 'Observaciones', type: 'select', options: ['SI', 'NO'] },
];

const FIELD_LABELS_LID = {
  fecha: 'Fecha', persona: 'Persona', sede_label: 'Sede', negocio: 'Negocio',
  actividad: 'Actividad', numeroasistentes: 'Asistentes',
  incumplimiento: 'Incumplimiento', descripcion: 'Descripción', comentario: 'Comentario',
  hora: 'Hora', observacion: 'Observación', fecharegistro: 'Fecha de registro', tematica: 'Temática',
  fecha_actividad: 'Fecha actividad real',
  metodo_firmas_asistentes: 'Método recogida firmas',
  email: 'Email responsable',
  asistentes: 'Listado asistentes',
  asistentes_count_real: 'Total asistentes (tabla)',
};

const _NOMBRE_AST_KEYS = Array.from({length:20}, (_,i) => `nombre_asistente_${String(i+1).padStart(2,'0')}`);

const SKIP_FIELDS_LID = new Set([
  'id','_id','objectid','globalid','_synced_at','_layer','sede_codigo','sede','centro',
  ..._NOMBRE_AST_KEYS,
  'asistentes_detail',
]);
const LONG_TEXT_FIELDS_LID = new Set(['descripcion', 'observacion', 'comentario', 'tematica', 'actividades', 'asistentes']);

const formatFieldValueLid = (value) => {
  if (typeof value === 'number' && value > 1_000_000_000_000) {
    return new Date(value).toLocaleString('es-ES', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  }
  return String(value).replace(/_/g, ' ');
};

function ExpandedDetailLid({ row }) {
  // Named attendees from form fields (nombre_asistente_01-20)
  const namedAttendees = _NOMBRE_AST_KEYS.map(k => row[k]).filter(Boolean);
  // Detailed attendees from the related asistentes_ table
  const detailAttendees = Array.isArray(row.asistentes_detail) ? row.asistentes_detail : [];

  const entries = Object.entries(row).filter(
    ([k, v]) => !SKIP_FIELDS_LID.has(k.toLowerCase()) && !SKIP_FIELDS_LID.has(k) && v != null && v !== '' && v !== '-'
  );
  const longEntries = entries.filter(([k]) => LONG_TEXT_FIELDS_LID.has(k));
  const shortEntries = entries.filter(([k]) => !LONG_TEXT_FIELDS_LID.has(k));
  return (
    <div style={{ background: 'var(--vz-bg-elevated)' }}>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-3 p-4">
        {shortEntries.map(([key, value]) => (
          <div key={key} className="min-w-0">
            <p className="text-[9px] uppercase tracking-wider font-semibold mb-0.5" style={{ color: 'var(--vz-text-muted)' }}>
              {FIELD_LABELS_LID[key] || key.replace(/_/g, ' ')}
            </p>
            <p className="text-xs truncate" style={{ color: 'var(--vz-text-secondary)' }} title={formatFieldValueLid(value)}>
              {formatFieldValueLid(value)}
            </p>
          </div>
        ))}
      </div>
      {longEntries.length > 0 && (
        <div className="px-4 pb-4 space-y-3 border-t" style={{ borderColor: 'var(--vz-border-subtle)' }}>
          {longEntries.map(([key, value]) => (
            <div key={key}>
              <p className="text-[9px] uppercase tracking-wider font-semibold mb-1" style={{ color: 'var(--vz-text-muted)' }}>
                {FIELD_LABELS_LID[key] || key.replace(/_/g, ' ')}
              </p>
              <p className="text-xs leading-relaxed whitespace-pre-wrap" style={{ color: 'var(--vz-text-secondary)' }}>
                {String(value)}
              </p>
            </div>
          ))}
        </div>
      )}
      {/* Named attendees from form fields */}
      {namedAttendees.length > 0 && (
        <div className="px-4 pb-3 border-t" style={{borderColor:'var(--vz-border-subtle)'}}>
          <p className="text-[10px] uppercase tracking-wider font-bold mt-2 mb-2" style={{color:'var(--vz-text-muted)'}}>
            Asistentes nominales ({namedAttendees.length})
          </p>
          <div className="flex flex-wrap gap-1.5">
            {namedAttendees.map((name, i) => (
              <span key={i} className="text-xs px-2 py-0.5 rounded-full" style={{background:'var(--vz-bg-card)',border:'1px solid var(--vz-border)',color:'var(--vz-text-secondary)'}}>{name}</span>
            ))}
          </div>
        </div>
      )}
      {/* Detailed attendees from related table (asistentes_) */}
      {detailAttendees.length > 0 && (
        <div className="px-4 pb-3 border-t" style={{borderColor:'var(--vz-border-subtle)'}}>
          <p className="text-[10px] uppercase tracking-wider font-bold mt-2 mb-2" style={{color:'var(--vz-text-muted)'}}>
            Registro de firmas ({detailAttendees.length})
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1">
            {detailAttendees.map((a, i) => (
              <div key={i} className="flex items-center gap-1.5 text-xs" style={{color:'var(--vz-text-secondary)'}}>
                <span className={a.tiene_firma ? 'text-green-500' : 'text-gray-400'} title={a.tiene_firma ? 'Firmado' : 'Sin firma'}>●</span>
                <span className="truncate">{a.nombre || '—'}</span>
                {a.dni && <span className="text-[9px]" style={{color:'var(--vz-text-muted)'}}>{a.dni}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
      {(row.objectid || row.OBJECTID) && (
        <div className="px-4 pb-3 flex items-center gap-2 border-t" style={{borderColor:'var(--vz-border-subtle)'}}>
          <PhotoViewerButton module="liderazgo" objectid={row.objectid || row.OBJECTID} />
        </div>
      )}
      <TrazabilidadFooter row={row} />
    </div>
  );
}

function LiderazgoTable({ filters = {} }) {
  const [page, setPage] = useState(1);
  const [expandedRow, setExpandedRow] = useState(null);
  const globalFilters = useFilterParams();
  const combinedFilters = { ...globalFilters, ...filters, page, limit: 50 };
  const { token, user } = useUserStore();
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const queryClient = useQueryClient();

  // Reset to page 1 when filters change
  const filtersKey = JSON.stringify(filters);
  React.useEffect(() => { setPage(1); setExpandedRow(null); }, [filtersKey]);

  const { data: listData, isLoading } = useQuery({
    queryKey: ['liderazgo-list', combinedFilters],
    queryFn: () => fetchLiderazgo(combinedFilters),
  });

  const deleteRecord = async (objectid, e) => {
    e.stopPropagation();
    if (!window.confirm('¿Eliminar este registro? Esta acción no se puede deshacer.')) return;
    try {
      const tok = localStorage.getItem('valoriza_token');
      await axios.delete(`${API}/api/liderazgo/${objectid}`, { headers: { Authorization: `Bearer ${tok}` } });
      toast.success('Registro eliminado');
      queryClient.invalidateQueries({ queryKey: ['liderazgo-list'] });
    } catch { toast.error('Error al eliminar'); }
  };

  const rows  = listData?.data  || [];
  const total = listData?.total || 0;
  const totalPages = Math.ceil(total / 50);

  const COLS = ['', 'Día', 'Fecha', 'Persona', 'Sede', 'Negocio', 'Actividad', 'Asistentes', 'Incumplimiento', ''];

  const toggleRow = (i) => setExpandedRow(prev => prev === i ? null : i);

  return (
    <div data-testid="liderazgo-table-container">
      <div className="flex items-center justify-between px-1 mb-2">
        <span className="text-xs" style={{ color: 'var(--vz-text-muted)' }}>{total.toLocaleString('es-ES')} registros</span>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" className="h-7 w-7" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft size={14} /></Button>
          <span className="text-xs" style={{ color: 'var(--vz-text-secondary)' }}>{page} / {totalPages || 1}</span>
          <Button variant="ghost" size="icon" className="h-7 w-7" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}><ChevronRight size={14} /></Button>
        </div>
      </div>
      <ScrollArea className="h-[420px]">
        <Table>
          <TableHeader>
            <TableRow style={{ borderColor: 'var(--vz-border-subtle)' }}>
              {COLS.map((h, idx) => (
                <TableHead key={idx} className="text-[10px] uppercase tracking-wider font-montserrat sticky top-0" style={{ color: 'var(--vz-text-muted)', background: 'var(--vz-bg-card)', width: idx === 0 ? 28 : undefined }}>{h}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading
              ? Array.from({ length: 7 }).map((_, i) => (
                  <TableRow key={i} style={{ borderColor: 'var(--vz-border-subtle)' }}>
                    {COLS.map((_, j) => <TableCell key={j}><div className="h-3 rounded animate-pulse" style={{ background: 'var(--vz-bg-elevated)', width: '80%' }} /></TableCell>)}
                  </TableRow>
                ))
              : rows.length === 0
                ? <TableRow><TableCell colSpan={9} className="text-center text-xs py-8" style={{ color: 'var(--vz-text-muted)' }}>Sin registros para este filtro</TableCell></TableRow>
                : rows.map((row, i) => (
                    <React.Fragment key={i}>
                      <TableRow
                        onClick={() => toggleRow(i)}
                        className="hover:bg-[#132860] transition-colors duration-200 cursor-pointer"
                        style={{ borderColor: 'var(--vz-border-subtle)', background: expandedRow === i ? 'rgba(0,200,83,0.05)' : undefined }}
                        data-testid="liderazgo-table-row"
                      >
                        <TableCell className="pl-3 pr-1 w-7">
                          <ChevronDown size={12} className="transition-transform duration-200" style={{ color: 'var(--vz-text-muted)', transform: expandedRow === i ? 'rotate(0deg)' : 'rotate(-90deg)' }} />
                        </TableCell>
                        <TableCell className="text-[10px] font-medium w-8 text-center" style={{ color: 'var(--vz-text-muted)' }}>{getDia(row.fecha)}</TableCell>
                        <TableCell className="text-xs font-mono-custom whitespace-nowrap" style={{ color: 'var(--vz-text-muted)' }}>{row.fecha || '-'}</TableCell>
                        <TableCell className="text-xs font-medium max-w-[130px] truncate" style={{ color: 'var(--vz-text-primary)' }}>{row.persona || '-'}</TableCell>
                        <TableCell className="text-xs max-w-[150px]" style={{ color: 'var(--vz-text-secondary)' }} title={(row.sede_label || (row.sede ? row.sede.replace(/_/g, ' ') : row.centro) || '') + (row.sede_codigo ? ` · ${row.sede_codigo}` : '')}>
                          <div className="leading-tight">
                            <div className="truncate">{row.sede_label || (row.sede ? row.sede.replace(/_/g, ' ') : null) || row.centro || '-'}</div>
                            {row.sede_codigo && <div className="text-[10px] font-mono opacity-50">{row.sede_codigo}</div>}
                          </div>
                        </TableCell>
                        <TableCell className="text-xs max-w-[110px] truncate" style={{ color: 'var(--vz-text-secondary)' }}>{row.negocio ? row.negocio.charAt(0).toUpperCase() + row.negocio.slice(1) : '-'}</TableCell>
                        <TableCell className="text-xs max-w-[130px] truncate" style={{ color: 'var(--vz-text-secondary)' }}>{row.actividad ? row.actividad.replace(/_/g, ' ').replace(/,/g, ' / ') : '-'}</TableCell>
                        <TableCell className="text-xs text-right tabular-nums font-bold" style={{ color: '#AB47BC' }}>{row.numeroasistentes ?? '-'}</TableCell>
                        <TableCell>
                          <Badge className="text-[10px]" style={{
                            background: row.incumplimiento === 'SI' ? 'rgba(255,82,82,0.15)' : 'rgba(0,200,83,0.12)',
                            color: row.incumplimiento === 'SI' ? '#FF5252' : '#00C853',
                            border: `1px solid ${row.incumplimiento === 'SI' ? 'rgba(255,82,82,0.3)' : 'rgba(0,200,83,0.25)'}`,
                          }}>{row.incumplimiento || 'NO'}</Badge>
                        </TableCell>
                        <TableCell className="text-center pr-2 w-6" title={row.has_foto ? 'Tiene foto adjunta' : ''}>
                          <div className="flex items-center gap-1 justify-end">
                            {row.has_foto && <Camera size={12} style={{ color: '#4A9EFF' }} />}
                            {isAdmin && (
                              <button
                                onClick={(e) => deleteRecord(row.objectid || row.OBJECTID, e)}
                                className="opacity-30 hover:opacity-100 transition-opacity"
                                title="Eliminar registro"
                                data-testid="lid-delete-btn"
                              >
                                <Trash2 size={11} className="text-red-400" />
                              </button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                      {expandedRow === i && (
                        <TableRow data-testid="liderazgo-expanded-row" style={{ borderColor: 'var(--vz-border-subtle)' }}>
                          <TableCell colSpan={10} className="p-0">
                            <ExpandedDetailLid row={row} />
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))
            }
          </TableBody>
        </Table>
      </ScrollArea>
    </div>
  );
}

export default function Liderazgo() {
  const globalFilters = useFilterParams();
  const { token } = useUserStore();
  const [moduleFilters, setModuleFilters] = useState({});
  const mergedFilters = useMemo(() => ({ ...globalFilters, ...moduleFilters }), [globalFilters, moduleFilters]);

  const handleFilterChange = (key, value) => setModuleFilters(prev => ({ ...prev, [key]: value }));
  const handleReset = () => setModuleFilters({});

  const enabled = !!token;
  const { data: topPersonas, isLoading: topLoading    } = useQuery({ queryKey: ['lid-top',      mergedFilters], queryFn: () => fetchLiderazgoTopPersonas({ ...mergedFilters, limit: 2000 }), enabled });
  const { data: porActividad,isLoading: actLoading    } = useQuery({ queryKey: ['lid-actividad', mergedFilters], queryFn: () => fetchLiderazgoPorActividad(mergedFilters), enabled });
  const { data: porSede,     isLoading: sedeLoading   } = useQuery({ queryKey: ['lid-sede',      mergedFilters], queryFn: () => fetchLiderazgoPorSede(mergedFilters), enabled });
  const { data: resumen,     isLoading: resumenLoading } = useQuery({ queryKey: ['lid-resumen',   mergedFilters], queryFn: () => fetchLiderazgoResumen(mergedFilters), staleTime: 60000, enabled });
  const { data: actStats,    isLoading: actStatsLoading } = useQuery({
    queryKey: ['lid-actividad-stats', mergedFilters],
    queryFn: async () => {
      const tok = localStorage.getItem('valoriza_token');
      const params = new URLSearchParams();
      if (mergedFilters.desde) params.append('desde', mergedFilters.desde);
      if (mergedFilters.hasta) params.append('hasta', mergedFilters.hasta);
      if (mergedFilters.sede)  params.append('sede',  mergedFilters.sede);
      if (mergedFilters.negocio) params.append('negocio', mergedFilters.negocio);
      if (mergedFilters.delegacion) params.append('delegacion', mergedFilters.delegacion);
      const r = await axios.get(`${API}/api/liderazgo/por-actividad-stats?${params}`, { headers: { Authorization: `Bearer ${tok}` } });
      return r.data;
    },
    enabled,
  });

  const { valid: sedeFiltered, sinSedeCount } = useMemo(() => filterSinSede(porSede || [], 'sede', 'total'), [porSede]);
  const sedeDisclaimer = sinSedeDisclaimer(sinSedeCount);

  return (
    <div data-testid="liderazgo-page">
      <GlobalFilterBar />
      <div className="px-4 lg:px-6 py-6 space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-montserrat font-bold text-lg" style={{ color: 'var(--vz-text-primary)' }}>Liderazgo en Seguridad</h2>
            <p className="text-xs mt-0.5" style={{ color: 'var(--vz-text-muted)' }}>Actividades de liderazgo registradas</p>
          </div>
          <ExportButtons tipo="liderazgo" filters={mergedFilters} />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="lid-kpi-row">
          <KpiGlowCard
            title="Total Actividades"
            value={resumen?.total}
            color="#00C853"
            icon={ClipboardList}
            loading={resumenLoading}
            delta={resumen?.delta_pct}
            sub={resumen?.delta_pct != null ? `vs mismo período ${new Date().getFullYear() - 1}` : 'Registros con filtro activo'}
          />
          <KpiGlowCard title="Personas que realizan actividades" value={resumen?.personas ?? '—'} sub="Responsables distintos" color="#AB47BC" icon={Users} loading={resumenLoading} />
          <KpiGlowCard title="Total asistentes" value={resumen?.asistentes_total ?? '—'} sub="Personas impactadas (suma)" color="#FF7043" icon={Users} loading={resumenLoading} tooltip="Suma total del campo 'Número de asistentes' en todos los registros del filtro activo." />
          <KpiGlowCard title="Centros con actividades" value={resumen?.centros ?? '—'} sub="Centros de trabajo distintos" color="#4A9EFF" icon={Building2} loading={resumenLoading} />
          <KpiGlowCard
            title="Valoración media"
            value={resumen?.valoracion_media != null ? `${resumen.valoracion_media.toLocaleString('es-ES', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} ★` : '—'}
            sub={resumen?.valoracion_total ? `Sobre 5 · ${resumen.valoracion_total.toLocaleString('es-ES')} valoradas` : 'Sin valoraciones'}
            color="#FFC107"
            icon={Star}
            loading={resumenLoading}
            tooltip="Media de la 'Valoración global' (1 a 5 estrellas) de las actividades valoradas en el filtro activo."
            data-testid="lid-kpi-valoracion"
          />
          <KpiGlowCard
            title="Duración media"
            value={resumen?.duracion_media_min != null ? `${resumen.duracion_media_min} min` : '—'}
            sub={resumen?.duracion_total ? `${resumen.duracion_total.toLocaleString('es-ES')} con horario` : 'Sin horario registrado'}
            color="#26C6DA"
            icon={Clock}
            loading={resumenLoading}
            tooltip="Media de duración (hora fin − hora inicio) de las actividades con horario registrado."
            data-testid="lid-kpi-duracion"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <ChartCard title="Top 10 personas" loading={topLoading} isEmpty={!topPersonas?.length}>
            <HorizontalBarChart data={(topPersonas || []).map((p) => ({ name: p.persona, total: p.total }))} nameKey="name" dataKey="total" color="#00C853" height={260} />
          </ChartCard>

          <ChartCard title="Por tipo de actividad" loading={actLoading} isEmpty={!porActividad?.length}>
            <HorizontalBarChart data={(porActividad || []).map((a) => ({ name: a.actividad, total: a.total }))} nameKey="name" dataKey="total" color="#AB47BC" height={260} />
          </ChartCard>
        </div>

        <ChartCard title="Actividades por sede" loading={sedeLoading} isEmpty={!sedeFiltered.length}>
          <HorizontalBarChart data={sedeFiltered.slice(0, 12).map((s) => ({ name: s.sede, total: s.total }))} nameKey="name" dataKey="total" color="#4A9EFF" height={280} />
          {sedeDisclaimer && (
            <div className="flex items-start gap-1.5 mt-2 px-1">
              <Info size={11} className="flex-shrink-0 mt-0.5" style={{ color: 'var(--vz-text-muted)' }} />
              <p className="text-[10px]" style={{ color: 'var(--vz-text-muted)' }}>{sedeDisclaimer}</p>
            </div>
          )}
        </ChartCard>

        <div className="rounded-2xl border p-4 space-y-4" style={{ background: 'var(--vz-bg-card)', borderColor: 'var(--vz-border-subtle)' }}>
          <h3 className="font-montserrat text-xs font-bold uppercase tracking-[0.16em]" style={{ color: 'var(--vz-text-secondary)' }}>Registros Liderazgo</h3>
          <ModuleFilterBar filters={moduleFilters} onChange={handleFilterChange} onReset={handleReset} filterDefs={LID_FILTER_DEFS} />
          <LiderazgoTable filters={moduleFilters} />
        </div>

        {/* Acciones por Actividad — nueva sección */}
        <div className="rounded-2xl border p-4 space-y-3" style={{ background: 'var(--vz-bg-card)', borderColor: 'var(--vz-border-subtle)' }} data-testid="lid-actividad-stats">
          <div className="flex items-center gap-2 mb-1">
            <Activity size={15} style={{ color: '#AB47BC' }} />
            <h3 className="font-montserrat text-xs font-bold uppercase tracking-[0.16em]" style={{ color: 'var(--vz-text-secondary)' }}>Resumen por tipo de actividad</h3>
          </div>
          {actStatsLoading ? (
            <div className="flex items-center gap-2 py-4">
              <div className="w-4 h-4 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: '#AB47BC', borderTopColor: 'transparent' }} />
              <span className="text-xs" style={{ color: 'var(--vz-text-muted)' }}>Cargando...</span>
            </div>
          ) : (actStats || []).length === 0 ? (
            <p className="text-xs py-4 text-center" style={{ color: 'var(--vz-text-muted)' }}>Sin datos para los filtros seleccionados</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow style={{ borderColor: 'var(--vz-border-subtle)' }}>
                  {['Tipo de actividad', 'Acciones', 'Total asistentes', 'Sedes'].map((h) => (
                    <TableHead key={h} className="text-[10px] uppercase tracking-wider font-montserrat sticky top-0" style={{ color: 'var(--vz-text-muted)', background: 'var(--vz-bg-card)' }}>{h}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {(actStats || []).map((row, i) => (
                  <TableRow key={i} className="hover:bg-[#132860] transition-colors" style={{ borderColor: 'var(--vz-border-subtle)' }}>
                    <TableCell className="font-medium text-xs py-2 capitalize" style={{ color: 'var(--vz-text-primary)' }}>{row.actividad}</TableCell>
                    <TableCell className="text-xs py-2">
                      <Badge style={{ background: 'rgba(171,71,188,0.15)', color: '#AB47BC', border: '1px solid rgba(171,71,188,0.3)', fontSize: 11 }}>{row.acciones.toLocaleString('es-ES')}</Badge>
                    </TableCell>
                    <TableCell className="text-xs py-2" style={{ color: 'var(--vz-text-secondary)' }}>{(row.asistentes || 0).toLocaleString('es-ES')}</TableCell>
                    <TableCell className="text-xs py-2" style={{ color: 'var(--vz-text-secondary)' }}>{row.sedes}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    </div>
  );
}
